// The Today queue loader. Ports legacy loadInbox semantics (v20 lines
// ~1524-1578, 1610-1620) onto the store + envelope + fetchAll infrastructure.
//
// v_ct_queue_all has NO assigned_to column, so scope can't be applied in SQL.
// We client-join a light ref→owner map from cases (chunked .in() over the
// reference_numbers actually in the queue, batched — the healthcheck's 1000-row
// FAIL-OPEN fix: a single un-ranged select is silently capped at 1,000 rows by
// PostgREST, which past 1,000 cases would misclassify capped-out cases as
// unassigned and hide a nurse's OWN overdue items under 'mine' with no error).
// Each batch goes through fetchAll so even a batch that somehow exceeds the cap
// is fully retrieved.
//
// SAFETY, ported verbatim:
//  - a serious/AE queue item BYPASSES scope — never hidden by "My patients";
//  - a FAILED queue load surfaces (loadError on the slice) and NEVER renders a
//    false all-clear;
//  - a FAILED owner-join FAILS OPEN — the full queue still renders (with the
//    degradation flag) because hiding the whole work queue is worse than
//    briefly showing colleagues' items — and the failure is toasted, not
//    swallowed.

import { store, SLICES } from '../state/store.js';
import { getClient, fetchAll, applyProgrammeFilter, ok, fail } from '../db/client.js';
import { VIEWS, TABLES, QUEUE_SAFETY_TOKENS } from '../db/contract.js';
import { netErr } from '../errors.js';
import { toast } from '../ui/toast.js';
import { getScope, scopeMatch } from './scope.js';

export const OWNER_JOIN_BATCH = 200; // legacy .in() batch size for the owner-join

// ─── Pure classification (unit-tested without a DB) ────────────────────────

/**
 * The queue-row bucket driving BOTH the KPI counts and the filter tabs — one
 * function so the Overdue KPI can never disagree with what the Overdue tab
 * lists (legacy loadInbox counts + renderInbox grouping, kept in lockstep).
 *   unreachable → its own bucket
 *   sha_overdue → 'risk' (High risk)
 *   overdue_consent / missed_followup → 'overdue'
 *   past its due_at → 'overdue'
 *   due today → 'today'
 *   otherwise → 'waiting'
 * @param {any} it queue row (queue_type, due_at)
 * @param {Date} now
 * @returns {'today'|'overdue'|'risk'|'unreachable'|'waiting'}
 */
export function queueBucket(it, now) {
  const qt = (it.queue_type || '').toLowerCase();
  if (qt === 'unreachable') return 'unreachable';
  if (qt === 'sha_overdue') return 'risk';
  if (qt === 'overdue_consent' || qt === 'missed_followup') return 'overdue';
  const due = new Date(it.due_at);
  if (due < now) return 'overdue';
  if (due.toDateString() === now.toDateString()) return 'today';
  return 'waiting';
}

/**
 * SAFETY OVERRIDE predicate (legacy isSafetyQueueItem): adverse-event /
 * serious-safety items must never be hidden by a personal scope filter.
 * Matches WHOLE tokens of queue_type (split on anything non-alphanumeric)
 * against QUEUE_SAFETY_TOKENS — the convention is recorded in db/contract.js.
 * Legacy matched the substring 'ae' anywhere, so any future type that merely
 * contains those letters would have bypassed scope (review R-17).
 * @param {any} it
 * @returns {boolean}
 */
export function isSafetyQueueItem(it) {
  const qt = (it && it.queue_type ? String(it.queue_type) : '').toLowerCase();
  return qt.split(/[^a-z0-9]+/).some((tok) => QUEUE_SAFETY_TOKENS.includes(tok));
}

/**
 * The owner of a queue row, via the ref→owner map (legacy ownerOf). A row whose
 * reference_number has no matching case (e.g. a consent-review task) counts as
 * UNASSIGNED (null). A ref present with a null owner is also unassigned.
 * @param {any} it queue row
 * @param {Record<string, {owner: string|null}>} refInfo
 * @returns {string|null}
 */
export function ownerOf(it, refInfo) {
  const ref = it && it.reference_number;
  if (ref && refInfo && Object.prototype.hasOwnProperty.call(refInfo, ref)) {
    return refInfo[ref].owner || null;
  }
  return null;
}

/**
 * Enrich a raw queue row with the facts staff weigh before a pick-up: the
 * treating facility (the queue's own facility is often empty — fall back to the
 * case's treating_facility from the owner-join), county, and owner. Pure.
 * @param {any} it raw queue row
 * @param {Record<string, {facility: string|null, county: string|null, owner: string|null}>} refInfo
 * @returns {any} a new row with {facility, county, owner} folded in
 */
export function enrichRow(it, refInfo) {
  const info = (refInfo && refInfo[it.reference_number]) || {};
  return Object.assign({}, it, {
    facility: it.facility || info.facility || null,
    county: info.county || null,
    owner: ownerOf(it, refInfo),
  });
}

/**
 * Bucket counts over a row set (legacy loadInbox KPI loop). Keys match the
 * legacy tile ids: tod / ov / risk / unr / wait.
 * @param {any[]} rows
 * @param {Date} now
 * @returns {{tod: number, ov: number, risk: number, unr: number, wait: number}}
 */
export function countBuckets(rows, now) {
  const c = { tod: 0, ov: 0, risk: 0, unr: 0, wait: 0 };
  for (const it of rows || []) {
    const b = queueBucket(it, now);
    if (b === 'today') c.tod++;
    else if (b === 'overdue') c.ov++;
    else if (b === 'risk') c.risk++;
    else if (b === 'unreachable') c.unr++;
    else c.wait++;
  }
  return c;
}

/** An empty count block (used on a failed load so tiles read 0, not stale). */
export function emptyCounts() {
  return { tod: 0, ov: 0, risk: 0, unr: 0, wait: 0, legacyPool: 0 };
}

/**
 * Split an array into fixed-size batches (pure). Used for the chunked owner-join.
 * @template T
 * @param {T[]} arr
 * @param {number} size
 * @returns {T[][]}
 */
export function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < (arr || []).length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// ─── The loader ────────────────────────────────────────────────────────────

/**
 * Load the Today queue and publish SLICES.QUEUE. Never throws — a failure
 * lands as loadError on the slice so the view can render "couldn't load — tap
 * to retry", never a false all-clear.
 *
 * Slice shape: { rows, all, refInfo, counts, loadError, ownerJoinFailed, loaded }
 *  - loaded: a load has RESOLVED at least once (success OR failure). Until it
 *    is true the view must show a skeleton, never an empty queue — an absent
 *    slice used to render "All clear — you're on track" while the first
 *    request was still in flight (or hung), which is a false all-clear on a
 *    clinical work queue. Same rule as PATIENTS.loaded / casesLoaded().
 *  - rows: scope-filtered + enriched (what Today lists)
 *  - all: every enriched row (for the Legacy-pool count + scope switches)
 *  - counts: {tod,ov,risk,unr,wait,legacyPool}
 *  - ownerJoinFailed: true when scope could not be applied (fail-open: rows==all)
 *
 * @param {{client?: any, session?: any, storage?: any}} [deps] test injection
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function loadQueue(deps) {
  const client = (deps && deps.client) || getClient();
  const session = (deps && deps.session) || store.get(SLICES.SESSION) || {};
  const selfId = session.user && session.user.id;
  const role = session.role;
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const active = (store.get(SLICES.FILTERS) || {}).programme || 'all';

  // 1. The queue rows — programme-scoped on programme_code, ordered by due,
  // fetched COMPLETE past the 1,000-row cap.
  const qRes = await fetchAll(() =>
    applyProgrammeFilter(
      client.from(VIEWS.CT_QUEUE_ALL).select('*', { count: 'exact' }),
      'programme_code',
      active,
      prog.byCode || {}
    ).order('due_at', { ascending: true })
  );
  if (!qRes.ok) {
    // Surface — never a false all-clear. Tiles read 0, the list shows the error.
    store.set(SLICES.QUEUE, {
      rows: [],
      all: [],
      refInfo: {},
      counts: emptyCounts(),
      loadError: qRes.error,
      ownerJoinFailed: false,
      loaded: true,
    });
    return fail(qRes.error);
  }
  const all = qRes.data;

  // 2. Owner-join: fetch ONLY the refs in the queue, in chunked .in() batches
  // (each through fetchAll). Fail OPEN on any batch error.
  const refs = [...new Set(all.map((it) => it.reference_number).filter(Boolean))];
  /** @type {Record<string, {facility: string|null, county: string|null, owner: string|null}>} */
  const refInfo = {};
  let ownerJoinFailed = false;
  /** @type {any} */
  let ownErr = null;
  for (const batch of chunk(refs, OWNER_JOIN_BATCH)) {
    const r = await fetchAll(() =>
      client
        .from(TABLES.CASES)
        .select('reference_number,assigned_to,treating_facility,county', { count: 'exact' })
        .in('reference_number', batch)
    );
    if (!r.ok) {
      ownerJoinFailed = true;
      ownErr = r.error;
      break;
    }
    for (const row of r.data) {
      if (row.reference_number) {
        refInfo[row.reference_number] = {
          facility: row.treating_facility || null,
          county: row.county || null,
          owner: row.assigned_to || null,
        };
      }
    }
  }

  // 3. Enrich every row.
  const enrichedAll = all.map((it) => enrichRow(it, refInfo));

  // 4. Scope filter (safety items always pass). Fail-open: when the owner-join
  // failed we can't trust the map, so show the full queue.
  const scope = getScope(selfId, role, deps && deps.storage);
  const rows = ownerJoinFailed
    ? enrichedAll
    : enrichedAll.filter((it) => isSafetyQueueItem(it) || scopeMatch(ownerOf(it, refInfo), scope, selfId));

  // 5. Counts over the SCOPED set; Legacy-pool count over ALL rows (ownerless).
  const counts = Object.assign(countBuckets(rows, new Date()), {
    legacyPool: ownerJoinFailed ? 0 : enrichedAll.filter((it) => !ownerOf(it, refInfo)).length,
  });

  store.set(SLICES.QUEUE, { rows, all: enrichedAll, refInfo, counts, loadError: null, ownerJoinFailed, loaded: true });

  // Surface the owner-join failure (never swallow). Legacy only toasts when the
  // hidden filtering actually mattered — i.e. not already on 'all'.
  if (ownerJoinFailed && scope.mode !== 'all') {
    toast(netErr(ownErr, 'Could not load assignments — showing the full queue'), 'err');
  }

  return ok({ rows, counts, ownerJoinFailed });
}
